import { useState, useRef, useEffect, useCallback } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Sparkles, Mic, Square, Send, Loader2, Check, X, AlertTriangle,
  History as HistoryIcon, RotateCcw, ArrowUpRight, Lightbulb, Zap, Plus, Trash2, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import AIActionDetailDrawer from "@/components/AIActionDetailDrawer";

const ACTION_LABELS = {
  create_work_entry: "Saisir des heures travaillées",
  create_absence: "Créer une absence",
  create_client: "Créer un client",
  update_client: "Modifier un client",
  create_order: "Créer une commande",
  update_order: "Modifier une commande",
  register_payment: "Enregistrer un paiement",
  refund: "Remboursement",
  create_sav: "Créer un SAV",
  update_sav: "Modifier un SAV",
  close_sav: "Clôturer un SAV",
  create_admin_task: "Créer une tâche administrative",
  close_admin_task: "Clore une tâche administrative",
  update_material_price: "Modifier le prix d'un produit",
  create_bonus: "Accorder une prime",
  edit_employee: "Modifier un salarié",
  disable_employee: "Sortie d'un salarié",
  create_equipment: "Ajouter un équipement",
  edit_equipment: "Modifier un équipement",
  retire_equipment: "Retirer un équipement",
  create_life_event: "Événement marquant",
  // Phase 80
  create_training: "Ajouter une formation",
  create_pv: "Ajouter un PV",
  adjust_cp_rtt: "Ajuster CP / RTT",
  create_subcontractor: "Créer un sous-traitant",
  assign_subcontractor_to_order: "Attribuer à un sous-traitant",
  create_sub_life_event: "Événement sous-traitant",
  create_supplier: "Créer un fournisseur",
  create_supplier_product: "Ajouter un produit fournisseur",
  lock_order_material: "Verrouiller une matière",
  send_deposit_reminder: "Relance acompte",
  toggle_order_paid: "Marquer payée / impayée",
  hide_order: "Masquer / réafficher une commande",
  put_order_on_hold: "Mettre en attente",
  create_seller: "Créer un vendeur",
  add_vehicle_maintenance: "Entretien véhicule",
  update_vehicle_inspection: "CT / antipollution",
  create_backlog_task: "Tâche backlog",
  assign_admin_task: "Assigner tâche admin",
  // Phase 81
  mark_order_completed: "Clôturer une commande",
  cancel_order: "Annuler une commande",
  update_order_planning_constraints: "Contraintes planning",
  create_supplier_price: "Nouveau prix fournisseur",
  add_stock_entry: "Mouvement de stock",
  edit_supplier: "Modifier un fournisseur",
  edit_supplier_product: "Modifier un produit fournisseur",
  create_salary_entry: "Nouvelle rémunération",
  edit_training: "Modifier une formation",
  edit_subcontractor: "Modifier un sous-traitant",
  disable_subcontractor: "Désactiver un sous-traitant",
  create_annual_objective: "Objectif annuel",
  create_travel_bonus_rate: "Grille prime déplacement",
  complete_admin_occurrence: "Cocher une échéance",
  create_recurring_task: "Tâche récurrente",
  create_order_type: "Nouveau type de commande",
  create_client_profile_from_order: "Fiche CRM depuis commande",
  create_contract: "Nouveau contrat / charge fixe",
  none: "Non actionnable",
};

const EXAMPLES = [
  "Stéphane Beuroulle a travaillé sur la commande TEST-202604-0001 du 16 mars au 21 mars",
  "Le prix de la peinture acrylique a changé à 45,50 € le pot",
  "Jean-Paul est en congé payé du 8 au 19 avril",
  "Crée un SAV chez Dupont : tâche recolle sur le chantier DUPONT-202603-0003, 2 jours avec 2 opérateurs",
  "Acompte de 3000 € encaissé aujourd'hui sur la commande MARTIN-202603-0001",
  "Change la marge cible de la commande XYZ à 55 %",
];

function confidenceColor(c) {
  if (c >= 0.75) return "bg-emerald-100 text-emerald-800 border-emerald-300";
  if (c >= 0.45) return "bg-amber-100 text-amber-800 border-amber-300";
  return "bg-red-100 text-red-800 border-red-300";
}

const ActionPreview = ({ action, onConfirm, onCancel, executing }) => {
  if (!action) return null;
  const label = ACTION_LABELS[action.action_type] || action.action_type;
  const notActionable = action.action_type === "none" || action.confidence < 0.3;

  return (
    <Card className="border-2 border-primary/30" data-testid="ai-action-preview">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          Aperçu de l'action proposée
          <Badge className={`${confidenceColor(action.confidence)} ml-auto font-mono`}>
            {Math.round((action.confidence || 0) * 100)}% confiance
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Type</div>
          <div className="font-semibold" data-testid="ai-action-type">{label}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Résumé</div>
          <div data-testid="ai-action-summary">{action.summary || "—"}</div>
        </div>

        {action.ambiguities?.length > 0 && (
          <Alert variant="destructive" className="bg-amber-50 text-amber-900 border-amber-300">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Ambiguïté détectée :</strong>
              <ul className="list-disc ml-4 mt-1">
                {action.ambiguities.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {action.warnings?.length > 0 && (
          <Alert className="bg-slate-50">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>À noter :</strong>
              <ul className="list-disc ml-4 mt-1">
                {action.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        <details className="text-xs border rounded p-2 bg-muted/20">
          <summary className="cursor-pointer font-mono text-muted-foreground">Détails techniques (params JSON)</summary>
          <pre className="mt-2 whitespace-pre-wrap font-mono text-[11px]">{JSON.stringify(action.params, null, 2)}</pre>
        </details>

        <Separator />
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={executing} data-testid="ai-cancel-action">
            <X className="h-4 w-4 mr-1" /> Annuler
          </Button>
          <Button
            onClick={onConfirm}
            disabled={executing || notActionable}
            className="bg-primary"
            data-testid="ai-confirm-action"
          >
            {executing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
            Valider et exécuter
          </Button>
        </div>
        {notActionable && (
          <p className="text-xs text-muted-foreground text-right">
            Action trop incertaine pour être exécutée automatiquement.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// ---- Macros dialog ----

const MacroDialog = ({ open, macro, onClose, onSaved }) => {
  const [form, setForm] = useState({ title: "", prompt: "", emoji: "⚡", auto_execute: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(macro
        ? { title: macro.title, prompt: macro.prompt, emoji: macro.emoji || "⚡", auto_execute: !!macro.auto_execute }
        : { title: "", prompt: "", emoji: "⚡", auto_execute: false });
    }
  }, [open, macro]);

  const submit = async () => {
    if (!form.title.trim() || !form.prompt.trim()) { toast.error("Titre et prompt requis"); return; }
    setSaving(true);
    try {
      if (macro) await api.aiUpdateMacro(macro.id, form);
      else await api.aiCreateMacro(form);
      toast.success(macro ? "Macro mise à jour" : "Macro créée");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="macro-dialog">
        <DialogHeader>
          <DialogTitle>{macro ? "Modifier la macro" : "Nouvelle macro"}</DialogTitle>
          <DialogDescription>
            Une macro est un raccourci réutilisable (ex : "Pointe mon équipe aujourd'hui chantier principal matin + après-midi").
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="w-20">
              <label className="text-xs text-muted-foreground">Emoji</label>
              <Input
                value={form.emoji}
                onChange={(e) => setForm((f) => ({ ...f, emoji: e.target.value.slice(0, 3) }))}
                maxLength={3}
                data-testid="macro-emoji"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-muted-foreground">Titre</label>
              <Input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Ex: Pointer l'équipe aujourd'hui"
                data-testid="macro-title"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Prompt</label>
            <Textarea
              rows={4}
              value={form.prompt}
              onChange={(e) => setForm((f) => ({ ...f, prompt: e.target.value }))}
              placeholder="Ex: Pointe Jean et Paul aujourd'hui sur la commande MARTIN-202603-0001, matin et après-midi."
              data-testid="macro-prompt"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving} data-testid="macro-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Main page ----

export const AIAssistantPage = () => {
  const [text, setText] = useState("");
  const [interpreting, setInterpreting] = useState(false);
  const [action, setAction] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [history, setHistory] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [macros, setMacros] = useState([]);
  const [macroDialog, setMacroDialog] = useState({ open: false, macro: null });
  const [drawer, setDrawer] = useState({ open: false, id: null });
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.aiHistory(10);
      setHistory(r.data || []);
    } catch { /* silent */ }
  }, []);
  const loadSuggestions = useCallback(async () => {
    try {
      const r = await api.aiSuggestions(5);
      setSuggestions(r.data?.suggestions || []);
    } catch { /* silent */ }
  }, []);
  const loadMacros = useCallback(async () => {
    try {
      const r = await api.aiListMacros();
      setMacros(r.data || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadHistory(); loadSuggestions(); loadMacros(); }, [loadHistory, loadSuggestions, loadMacros]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const r = await api.aiTranscribe(blob);
          const transcribed = r.data?.text || "";
          setText((prev) => prev ? `${prev} ${transcribed}` : transcribed);
          toast.success("Transcription terminée");
        } catch (e) {
          toast.error(getErrorMessage(e, "Transcription échouée"));
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("Micro inaccessible : " + e.message);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const interpret = async (override) => {
    const raw = typeof override === "string" ? override : text;
    if (!raw?.trim()) { toast.info("Entrez une instruction"); return; }
    setInterpreting(true);
    setAction(null);
    try {
      const r = await api.aiInterpret(raw.trim());
      setAction(r.data);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur d'interprétation"));
    } finally {
      setInterpreting(false);
    }
  };

  const executeAction = async () => {
    if (!action?.action_id) return;
    setExecuting(true);
    try {
      const r = await api.aiExecute(action.action_id);
      toast.success("Action exécutée");
      setAction(null);
      setText("");
      loadHistory();
      loadSuggestions();
      console.info("AI action result:", r.data);
    } catch (e) {
      toast.error(getErrorMessage(e, "Exécution échouée"));
    } finally {
      setExecuting(false);
    }
  };

  const cancelAction = async () => {
    if (action?.action_id) {
      try { await api.aiCancel(action.action_id); } catch { /* ignore */ }
    }
    setAction(null);
  };

  const reuseFromHistory = (h) => {
    setText(h.original_text || "");
    setAction(null);
  };

  const runMacro = async (m) => {
    setText(m.prompt);
    setAction(null);
    await interpret(m.prompt);
    if (m.auto_execute) {
      // Will be triggered via action state once interpret resolves
      // Note: we can't rely on the state update inside this function,
      // so the user still sees the preview card and just confirms.
    }
  };

  const deleteMacro = async (id) => {
    if (!window.confirm("Supprimer cette macro ?")) return;
    try {
      await api.aiDeleteMacro(id);
      toast.success("Macro supprimée");
      loadMacros();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    }
  };

  return (
    <div className="space-y-6 animate-in" data-testid="ai-assistant-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <Sparkles className="h-7 w-7 text-primary" /> Assistant IA
        </h1>
        <p className="text-muted-foreground mt-1">
          Dictez ou écrivez vos instructions — l'IA identifie l'action à effectuer et vous propose un aperçu avant de l'exécuter.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Votre instruction</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {suggestions.length > 0 && (
            <div className="flex items-start gap-2 flex-wrap" data-testid="ai-suggestions">
              <span className="text-xs text-muted-foreground flex items-center gap-1 mt-1.5">
                <Lightbulb className="h-3.5 w-3.5" /> Suggestions :
              </span>
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => { setText(s.text); setAction(null); }}
                  className="text-xs px-2.5 py-1 rounded-full border border-primary/20 bg-primary/5 hover:bg-primary/10 hover:border-primary/40 transition-colors truncate max-w-[320px]"
                  title={s.display_text || s.text}
                  data-testid={`ai-suggestion-${i}`}
                >
                  {(() => {
                    const label = s.display_text || s.text;
                    return label.length > 64 ? `${label.slice(0, 62)}…` : label;
                  })()}
                </button>
              ))}
            </div>
          )}

          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Ex : Stéphane a travaillé sur le chantier Martin du 16 au 21 mars."
            rows={4}
            disabled={interpreting || transcribing}
            data-testid="ai-input"
          />
          <div className="flex flex-wrap items-center gap-2">
            {!recording ? (
              <Button
                variant="outline"
                onClick={startRecording}
                disabled={interpreting || transcribing}
                data-testid="ai-mic-start"
              >
                {transcribing
                  ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Transcription…</>
                  : <><Mic className="h-4 w-4 mr-1" /> Dicter</>}
              </Button>
            ) : (
              <Button variant="destructive" onClick={stopRecording} data-testid="ai-mic-stop">
                <Square className="h-4 w-4 mr-1" /> Stop
              </Button>
            )}
            <Button
              onClick={() => interpret()}
              disabled={interpreting || transcribing || recording || !text.trim()}
              data-testid="ai-interpret"
            >
              {interpreting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              Interpréter
            </Button>
            {text && (
              <Button variant="ghost" onClick={() => { setText(""); setAction(null); }}>
                <RotateCcw className="h-4 w-4 mr-1" /> Effacer
              </Button>
            )}
            {recording && (
              <span className="text-sm text-red-600 flex items-center gap-1 ml-2">
                <span className="h-2 w-2 rounded-full bg-red-600 animate-pulse" /> Enregistrement…
              </span>
            )}
          </div>

          <div className="text-xs text-muted-foreground">
            <div className="font-semibold mb-1">Exemples :</div>
            <ul className="space-y-0.5">
              {EXAMPLES.map((ex, i) => (
                <li key={i}>
                  <button
                    type="button"
                    className="text-left hover:text-primary transition-colors"
                    onClick={() => { setText(ex); setAction(null); }}
                  >
                    • {ex}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>

      {action && (
        <ActionPreview
          action={action}
          onConfirm={executeAction}
          onCancel={cancelAction}
          executing={executing}
        />
      )}

      {/* Macros */}
      <Card data-testid="ai-macros-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary" /> Macros
              <Badge variant="outline" className="ml-1 text-xs font-mono">{macros.length}</Badge>
            </CardTitle>
            <Button size="sm" onClick={() => setMacroDialog({ open: true, macro: null })} data-testid="ai-macro-new">
              <Plus className="h-4 w-4 mr-1" /> Nouvelle macro
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {macros.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Aucune macro. Créez-en une pour accéder en un clic à vos instructions les plus fréquentes.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="ai-macros-list">
              {macros.map((m) => (
                <div
                  key={m.id}
                  className="group border rounded-md p-2.5 hover:border-primary/60 hover:bg-primary/5 transition-colors relative"
                  data-testid={`ai-macro-${m.id}`}
                >
                  <button
                    type="button"
                    onClick={() => runMacro(m)}
                    className="w-full text-left"
                    disabled={interpreting}
                    data-testid={`ai-macro-run-${m.id}`}
                  >
                    <div className="font-semibold text-sm flex items-center gap-1.5">
                      <span className="text-base leading-none">{m.emoji || "⚡"}</span>
                      <span className="truncate">{m.title}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 line-clamp-2" title={m.prompt}>
                      {m.prompt}
                    </div>
                  </button>
                  <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setMacroDialog({ open: true, macro: m })} data-testid={`ai-macro-edit-${m.id}`}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => deleteMacro(m.id)} data-testid={`ai-macro-delete-${m.id}`}>
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <HistoryIcon className="h-4 w-4" />
              Historique récent
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                try {
                  const token = localStorage.getItem("token");
                  const url = api.aiHistoryExportUrl();
                  const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                  if (!resp.ok) throw new Error("Export échoué");
                  const blob = await resp.blob();
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = `ai_history_${new Date().toISOString().slice(0,10)}.csv`;
                  a.click();
                  URL.revokeObjectURL(a.href);
                  toast.success("CSV téléchargé");
                } catch (_e) {
                  toast.error("Export CSV échoué");
                }
              }}
              data-testid="ai-history-export"
            >
              <ArrowUpRight className="h-4 w-4 mr-1" /> Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Aucun historique pour le moment.</p>
          ) : (
            <ul className="space-y-2" data-testid="ai-history">
              {history.map((h) => (
                <li
                  key={h.id}
                  className="border rounded-md p-2 text-sm flex items-start gap-2 hover:bg-muted/30 transition-colors"
                  data-testid={`ai-history-item-${h.id}`}
                >
                  <Badge className={`${
                    h.status === "executed" ? "bg-emerald-100 text-emerald-800" :
                    h.status === "cancelled" ? "bg-slate-100 text-slate-700" :
                    h.status === "failed" ? "bg-red-100 text-red-800" :
                    "bg-amber-100 text-amber-800"
                  } text-xs shrink-0`}>
                    {h.status}
                  </Badge>
                  <button
                    type="button"
                    onClick={() => setDrawer({ open: true, id: h.id })}
                    className="flex-1 min-w-0 text-left"
                    data-testid={`ai-history-open-${h.id}`}
                  >
                    <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                      <span>{ACTION_LABELS[h.action_type] || h.action_type}</span>
                      <span>•</span>
                      <span>{new Date(h.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</span>
                      {h.created_by_name && (
                        <>
                          <span>•</span>
                          <span>par <strong>{h.created_by_name}</strong></span>
                        </>
                      )}
                      {h.executed_at && h.executed_by_name && h.executed_by_name !== h.created_by_name && (
                        <>
                          <span>•</span>
                          <span>exécuté par <strong>{h.executed_by_name}</strong></span>
                        </>
                      )}
                    </div>
                    <div className="truncate mt-0.5" title={h.original_text}>"{h.original_text}"</div>
                    {h.summary && <div className="text-xs text-muted-foreground italic">{h.summary}</div>}
                    {h.error && <div className="text-xs text-red-600 mt-0.5">⚠ {h.error}</div>}
                  </button>
                  <Button size="sm" variant="ghost" onClick={() => reuseFromHistory(h)} data-testid={`ai-history-reuse-${h.id}`}>
                    Reprendre
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <MacroDialog
        open={macroDialog.open}
        macro={macroDialog.macro}
        onClose={() => setMacroDialog({ open: false, macro: null })}
        onSaved={loadMacros}
      />

      <AIActionDetailDrawer
        actionId={drawer.id}
        open={drawer.open}
        onOpenChange={(o) => setDrawer((prev) => ({ ...prev, open: o }))}
        onMacroCreated={loadMacros}
      />
    </div>
  );
};

export default AIAssistantPage;
